import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';

export default function Strategy() {
  useScrollAnimation();

  useEffect(() => {
    document.title = 'Strategy | Audacious Quest PTE Ltd';
  }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      {/* Hero Section */}
      <section className="relative h-[420px] md:h-[500px] overflow-hidden scroll-animate">
        <img
          src="https://readdy.ai/api/search-image?query=Elegant%20marble%20spheres%20perfectly%20balanced%20on%20intersecting%20wooden%20beams%20creating%20a%20sophisticated%20kinetic%20sculpture%20art%20piece%2C%20minimalist%20composition%20against%20smooth%20warm%20neutral%20gray%20studio%20background%2C%20conceptual%20art%20representing%20balance%20strategy%20and%20precision%2C%20museum%20quality%20photography%20with%20soft%20diffused%20lighting&width=1600&height=500&seq=strategy-hero&orientation=landscape"
          alt="Strategise and Safeguard"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/40" />
        <div className="absolute inset-0 flex items-center justify-center">
          <h2 className="text-3xl md:text-5xl lg:text-6xl font-heading text-white text-center drop-shadow-lg px-4">
            Strategise and Safeguard
          </h2>
        </div>
      </section>

      {/* Strategy Text */}
      <section className="py-14 md:py-20 px-4 md:px-6 scroll-animate">
        <div className="max-w-4xl mx-auto">
          <p className="text-sm text-foreground-500 mb-2 tracking-wide">Strategy</p>
          <p className="text-lg md:text-xl text-foreground-800 leading-relaxed">
            At Audacious, we anticipate that innovative new technologies are being used to address the
            world&apos;s greatest concerns. There are several long-term growth themes driving technology
            demand, resulting in some highly attractive investment prospects.
          </p>
        </div>
      </section>

      {/* Vision Statement */}
      <section className="relative scroll-animate">
        <div className="relative h-[300px] md:h-[420px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Double%20exposure%20artistic%20portrait%20of%20professional%20businessman%20in%20formal%20suit%20silhouette%20merged%20with%20modern%20skyscraper%20city%20skyline%20at%20golden%20sunrise%2C%20warm%20amber%20light%20tones%2C%20inspirational%20corporate%20vision%20concept%2C%20clean%20high-key%20editorial%20photography%2C%20wide%20panoramic%20composition&width=1600&height=500&seq=about-vision-double&orientation=landscape"
            alt="Market Vision"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-black/40" />
          <div className="absolute inset-0 flex items-center justify-center px-6 md:px-12">
            <p className="text-lg md:text-2xl lg:text-3xl text-white text-center max-w-3xl leading-relaxed font-heading drop-shadow-lg">
              We are aware of how critical it is to react promptly to market developments in order to safeguard and increase your investment.
            </p>
          </div>
        </div>
      </section>

      {/* 3 Service Cards */}
      <section className="py-14 md:py-20 px-4 md:px-6 scroll-animate">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-heading text-primary-900 mb-8 md:mb-10">
            Our strategy are designed to
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {/* Network */}
            <div className="group">
              <div className="overflow-hidden mb-4 rounded-lg">
                <img
                  src="https://readdy.ai/api/search-image?query=Close%20up%20of%20professional%20businessman%20in%20dark%20suit%20carefully%20examining%20documents%20under%20warm%20amber%20desk%20lamp%20light%2C%20discovering%20hidden%20value%20and%20undervalued%20opportunities%2C%20moody%20corporate%20office%20setting%2C%20cinematic%20editorial%20photography%20with%20soft%20golden%20lighting%20and%20shallow%20depth%20of%20field&width=400&height=260&seq=strategy-identify-v3&orientation=landscape"
                  alt="Network"
                  className="w-full h-48 md:h-56 object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <h3 className="text-lg font-medium text-primary-900">Identify</h3>
              <p className="text-sm text-foreground-600 leading-relaxed mt-2">
                attractive prospects that we believe are undervalued
              </p>
            </div>

            {/* Assessment */}
            <div className="group">
              <div className="overflow-hidden mb-4 rounded-lg">
                <img
                  src="https://readdy.ai/api/search-image?query=Business%20strategist%20standing%20in%20modern%20glass%20office%20looking%20at%20large%20digital%20screen%20displaying%20upward%20trending%20growth%20charts%20and%20data%20analytics%2C%20exploring%20long-term%20market%20themes%2C%20warm%20ambient%20lighting%20with%20city%20skyline%20reflections%2C%20cinematic%20corporate%20editorial%20photography%20with%20moody%20atmospheric%20tones&width=400&height=260&seq=strategy-explore-v3&orientation=landscape"
                  alt="Assessment"
                  className="w-full h-48 md:h-56 object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <h3 className="text-lg font-medium text-primary-900">Explore</h3>
              <p className="text-sm text-foreground-600 leading-relaxed mt-2">
                Derive information from our team of strategists and experts on potential long-term growth themes
              </p>
            </div>

            {/* Support */}
            <div className="group">
              <div className="overflow-hidden mb-4 rounded-lg">
                <img
                  src="https://readdy.ai/api/search-image?query=Executive%20silhouette%20standing%20on%20modern%20rooftop%20terrace%20at%20dawn%20overlooking%20vast%20illuminated%20city%20skyline%2C%20determining%20future%20winners%20and%20strategic%20vision%2C%20warm%20golden%20sunrise%20light%20breaking%20through%2C%20cinematic%20editorial%20photography%20with%20deep%20blue%20and%20amber%20tones&width=400&height=260&seq=strategy-determine-v3&orientation=landscape"
                  alt="Support"
                  className="w-full h-48 md:h-56 object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <h3 className="text-lg font-medium text-primary-900">Determine</h3>
              <p className="text-sm text-foreground-600 leading-relaxed mt-2">
                We examine the finest options for your long-term portfolio and determine the winners of the next decade
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}